import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { combineLatest, Observable, switchMap } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { CommentsApiService } from '../comments-api.service';
import {
  AddComment,
  AddCommentSuccess,
  ClearComments,
  LoadComments,
  MarkMessageAsRead,
  SetComments,
  SetError,
  SetLoading,
} from './comments.actions';
import { Comment, MessageEntityType } from '../comments.model';
import { isEmpty, Logger } from '@aiwyn/shared';
import { ClientRequestsState } from '../../../state/client-requests.state';
import { CommentsStateModel } from './comments-state.model';
import { hasUnreadMessages } from '../comments.utils';
import { AuthService } from '@aiwyn/auth';
import { LaunchDarklyFlag, LaunchDarklyState } from '@aiwyn/config';
import { partition } from 'lodash';

/**
 * NGXS State for Comments Management
 * Manages comments for multiple entities (CLIENT_REQUEST_ITEM, ENGAGEMENT, JOB)
 */
@Injectable()
@State<CommentsStateModel>({
  name: 'comments',
  defaults: {
    commentsByEntity: {},
    loading: false,
    error: null,
  },
})
export class CommentsState {
  private readonly logger = inject(Logger);
  private readonly commentsApiService = inject(CommentsApiService);
  private readonly _store = inject(Store);
  private readonly _authService = inject(AuthService);

  /**
   * Load comments for one or more entities
   */
  @Action(LoadComments)
  loadComments(ctx: StateContext<CommentsStateModel>, action: LoadComments): Observable<Comment[]> {
    ctx.patchState({ loading: true, error: null });

    return combineLatest([
      this._store.select(ClientRequestsState.getSelectedClientRequestItems),
      this._store.select(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_CLIENT_REQUEST_ITEM_RESTRICTIONS)),
      this._authService.getUser().pipe(
        map(user => user?.getContactId()),
      ),
    ]).pipe(
      take(1),
      switchMap(([clientRequestItems, isItemRestrictionEnabled, contactId]) => {
        /** Possible restrictions to account for */
        if (action.entityType === MessageEntityType.CLIENT_REQUEST_ITEM && isItemRestrictionEnabled) {
          const [maskedItemIds, unmaskedItemIds] = partition(
            action.entityIds,
            id => {
              const item = clientRequestItems.find(item => item.itemId === id);
              return item && item.item.restricted && !item.item.assignees.some(assignee => assignee.contactId === contactId);
            },
          );

          if (isEmpty(maskedItemIds)) {
            return this.commentsApiService.getMessages(action.entityIds, action.entityType, action.limit);
          }

          return combineLatest([
            // Worst case scenario, results could be twice the limit. Not sure how to handle.
            this.commentsApiService.getMessages(maskedItemIds, action.entityType, action.limit, true),
            this.commentsApiService.getMessages(unmaskedItemIds, action.entityType, action.limit),
          ]).pipe(
            // combine masked and unmasked comments
            map(([maskedComments, unmaskedComments]) => [...maskedComments, ...unmaskedComments]),
            // sort by createdAt descending
            map((comments) => comments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())),
          );
        }

        /** Easy, breezy, beautiful standard path (no possible restrictions) */
        return this.commentsApiService.getMessages(action.entityIds, action.entityType, action.limit);
      }),
      map((comments) => {
        const clientRequestItems = this._store.selectSnapshot(ClientRequestsState.getSelectedClientRequestItems);
        return comments.map((comment) => {
          const clientRequestItem = clientRequestItems.find((item) => item.itemId === comment.entityId);
          return { ...comment, context: clientRequestItem?.item?.name ?? null };
        });
      }),
      tap({
        next: (comments) => {
          // Group comments by entityId
          const commentsByEntityId = comments.reduce(
            (acc, comment) => {
              const entityId = comment.entityId;
              if (!acc[entityId]) {
                acc[entityId] = [];
              }
              acc[entityId].push(comment);
              return acc;
            },
            {} as Record<number, Comment[]>,
          );

          // Dispatch SetComments for each entity
          if (Object.keys(commentsByEntityId).length > 0) {
            for (const [entityId, entityComments] of Object.entries(commentsByEntityId)) {
              ctx.dispatch(new SetComments(Number(entityId), action.entityType, entityComments));
            }
          } else {
            // Set existing ones as 0
            for (const entityId of action.entityIds) {
              ctx.dispatch(new SetComments(Number(entityId), action.entityType, []));
            }
          }
        },
        error: (error) => {
          this.logger.error('Error loading comments:', error);
          ctx.dispatch(new SetError(error.message || 'Failed to load comments'));
          ctx.patchState({ loading: false });
        },
      }),
    );
  }

  /**
   * Set comments in state
   */
  @Action(SetComments)
  setComments(ctx: StateContext<CommentsStateModel>, action: SetComments): void {
    const key = `${action.entityType}:${action.entityId}`;
    const state = ctx.getState();

    ctx.patchState({
      commentsByEntity: {
        ...state.commentsByEntity,
        [key]: action.comments,
      },
      loading: false,
      error: null,
    });
  }

  /**
   * Add a new comment (triggers API call)
   */
  @Action(AddComment)
  addComment(ctx: StateContext<CommentsStateModel>, action: AddComment): Observable<Comment> {
    const request = {
      entityType: action.payload.entityType,
      entityId: action.payload.entityId,
      content: action.payload.text,
      parentMessageId: action.payload.parentId ? parseInt(action.payload.parentId, 10) : null,
    };

    return this.commentsApiService.createMessage(request).pipe(
      tap({
        next: (newComment) => {
          ctx.dispatch(new AddCommentSuccess(newComment));
        },
        error: (error) => {
          console.error('Error adding comment:', error);
          ctx.dispatch(new SetError(error.message || 'Failed to add comment'));
        },
      }),
    );
  }

  /**
   * Add comment success - update state with new comment
   */
  @Action(AddCommentSuccess)
  addCommentSuccess(ctx: StateContext<CommentsStateModel>, action: AddCommentSuccess): void {
    const state = ctx.getState();
    const newComment = action.comment;
    const key = `${newComment.entityType}:${newComment.entityId}`;
    const currentComments = state.commentsByEntity[key] || [];

    // If it's a reply, add it to the parent's replies array
    if (newComment.replies && newComment.replies.length > 0) {
      // This is a parent comment with replies - shouldn't happen in AddCommentSuccess
      // Just add it to the list
      ctx.patchState({
        commentsByEntity: {
          ...state.commentsByEntity,
          [key]: [newComment, ...currentComments],
        },
      });
    } else {
      // Check if this is a reply by looking for parent in existing comments
      const updatedComments = this.addReplyToParent(currentComments, newComment);

      if (updatedComments !== currentComments) {
        // Reply was added to parent
        ctx.patchState({
          commentsByEntity: {
            ...state.commentsByEntity,
            [key]: updatedComments,
          },
        });
      } else {
        // New parent comment - add to beginning of list
        ctx.patchState({
          commentsByEntity: {
            ...state.commentsByEntity,
            [key]: [newComment, ...currentComments],
          },
        });
      }
    }
  }

  /**
   * Helper to add reply to parent comment
   * Recursively searches for the parent comment and adds the reply to its replies array
   */
  private addReplyToParent(comments: Comment[], reply: Comment): Comment[] {
    if (!reply.parentMessageId) {
      // Not a reply, return original comments
      return comments;
    }

    // Create a deep copy to avoid mutation
    return comments.map((comment) => {
      // Check if this is the parent comment
      if (comment.id === reply.parentMessageId) {
        // Add reply to this comment's replies array
        return {
          ...comment,
          replies: [...(comment.replies || []), reply],
        };
      }

      // Check nested replies recursively
      if (comment.replies && comment.replies.length > 0) {
        const updatedReplies = this.addReplyToParent(comment.replies, reply);
        if (updatedReplies !== comment.replies) {
          return {
            ...comment,
            replies: updatedReplies,
          };
        }
      }

      return comment;
    });
  }

  /**
   * Clear comments for a specific entity
   */
  @Action(ClearComments)
  clearComments(ctx: StateContext<CommentsStateModel>, action: ClearComments): void {
    const key = `${action.entityType}:${action.entityId}`;
    const state = ctx.getState();
    const { [key]: removed, ...rest } = state.commentsByEntity;

    ctx.patchState({
      commentsByEntity: rest,
    });
  }

  /**
   * Set loading state
   */
  @Action(SetLoading)
  setLoading(ctx: StateContext<CommentsStateModel>, action: SetLoading): void {
    ctx.patchState({ loading: action.loading });
  }

  /**
   * Set error state
   */
  @Action(SetError)
  setError(ctx: StateContext<CommentsStateModel>, action: SetError): void {
    ctx.patchState({ error: action.error });
  }

  /**
   * Mark a message as read by updating its isUnread flag
   * Recursively updates the message in the comments tree
   * If the message has unread replies, it remains marked as unread
   * Also updates parent messages to reflect changes in their children
   */
  @Action(MarkMessageAsRead)
  markMessageAsRead(ctx: StateContext<CommentsStateModel>, action: MarkMessageAsRead): void {
    const key = `${action.entityType}:${action.entityId}`;
    const state = ctx.getState();
    const currentComments = state.commentsByEntity[key] || [];

    // Recursively find and update the message, and update parent status based on children
    const updateMessageReadStatus = (comments: Comment[]): { comments: Comment[]; wasUpdated: boolean } => {
      let wasUpdated = false;

      const updatedComments = comments.map((comment) => {
        if (comment.id === action.messageId) {
          wasUpdated = true;
          const updatedComment = {
            ...comment,
            isUnread: false,
          };

          // Check if any replies are unread - if so, parent should remain unread
          const hasUnreadReplies =
            updatedComment.replies && updatedComment.replies.length > 0 && updatedComment.replies.some((reply) => hasUnreadMessages(reply));

          return {
            ...updatedComment,
            isUnread: hasUnreadReplies,
          };
        }

        // Check if the message is in the children
        if (comment.replies && comment.replies.length > 0) {
          const result = updateMessageReadStatus(comment.replies);

          // If a child was updated, update this parent's status
          if (result.wasUpdated) {
            wasUpdated = true;
            const hasUnreadReplies = result.comments.some((reply) => hasUnreadMessages(reply));

            return {
              ...comment,
              replies: result.comments,
              // Parent should only be unread if it's unread itself OR has unread replies
              isUnread: hasUnreadReplies,
            };
          }

          return {
            ...comment,
            replies: result.comments,
          };
        }

        return comment;
      });

      return { comments: updatedComments, wasUpdated };
    };

    const updateResult = updateMessageReadStatus(currentComments);

    ctx.patchState({
      commentsByEntity: {
        ...state.commentsByEntity,
        [key]: updateResult.comments,
      },
    });
  }
}
